import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import {
    urlParamReplicaId,
    urlParamUniversalId,
    urlProjectDocumentAddFiles,
    urlUploadFile,
} from '../../Constants';
import { IAction, SliceProps } from '../store';
import { IAddDocumentFilesDialogState } from '../../components/General/Dialogs/AddDocumentFilesDialog/AddDocumentFilesDialog';
import { GlobalVariables } from '../../GlobalVariables';
import { safeJsonParse } from '../../util/DataUtils';
import { IDetailedErrorMessage } from '../../types';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IFileUploadState = {
    addDocumentFilesDialogState: {
        config: null,
        loading: false,
        result: null,
        error: null,
    },
    filesPathes: [],
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    showAddDocumentFilesDialog(
        state: IFileUploadState,
        action: IAction<{
            universalId: string;
            docTypeKey: string;
        } | null>,
    ) {
        state.addDocumentFilesDialogState.config = action.payload;
    },
    resetAddDocumentFilesState(state: IFileUploadState) {
        state.addDocumentFilesDialogState = initialState.addDocumentFilesDialogState;
    },
    resetUtilsFileUploadState(state: IFileUploadState) {
        Object.assign(state, initialState);
    },
    resetErrorUploadFilesGeneral(state: IFileUploadState) {
        state.error = null;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    uploadFile: async (file: File): Promise<string> => {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const response = await apiFetch(urlUploadFile, request);

            return response.ok
                ? Promise.resolve(await response.text())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    uploadFiles: async (files: File[], thunkApi): Promise<void> => {
        try {
            await Promise.all(files.map(async (file: File) => thunkApi.dispatch(uploadFile(file))));

            return Promise.resolve();
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    addDocumentFiles: async (config: {
        replicaId: string;
        universalId: string;
        docTypeKey: string;
        files: string[];
        fileDataModels: string[];
    }): Promise<string> => {
        try {
            const { replicaId, universalId, docTypeKey, files, fileDataModels } = config;

            const formData = new FormData();
            files.forEach(id => formData.append('files', GlobalVariables.getFileById(id)));
            fileDataModels.forEach(id =>
                formData.append('fileDataModels', GlobalVariables.getFileById(id)),
            );

            const url = urlProjectDocumentAddFiles
                .replace(urlParamReplicaId, replicaId)
                .replace(urlParamUniversalId, universalId);

            const request: RequestInit = {
                method: 'POST',
                headers: {
                    doctype: docTypeKey,
                },
                body: formData,
            };

            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.text())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IFileUploadState>) => {
        /**
         * Reducers for the 'uploadFile' thunk.
         */
        builder.addCase(uploadFile.pending, (state: Draft<IFileUploadState>) => {
            state.loading = true;
            state.error = null;
        });

        builder.addCase(
            uploadFile.fulfilled,
            (state: Draft<IFileUploadState>, action: IAction<string>) => {
                state.filesPathes = [...state.filesPathes, action.payload];
                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            uploadFile.rejected,
            (state: Draft<IFileUploadState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'uploadFiles' thunk.
         */
        builder.addCase(uploadFiles.pending, (state: Draft<IFileUploadState>) => {
            state.loading = true;
            state.error = null;
        });

        builder.addCase(uploadFiles.fulfilled, (state: Draft<IFileUploadState>) => {
            state.loading = false;
            state.error = null;
        });

        builder.addCase(
            uploadFiles.rejected,
            (state: Draft<IFileUploadState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );

        builder.addCase(addDocumentFiles.pending, (state: Draft<IFileUploadState>) => {
            state.addDocumentFilesDialogState.loading = true;
        });
        builder.addCase(addDocumentFiles.fulfilled, (state: Draft<IFileUploadState>, action) => {
            state.addDocumentFilesDialogState.loading = false;
            const { files, fileDataModels } = action.meta.arg;
            state.addDocumentFilesDialogState.result = files.length + fileDataModels.length;
        });
        builder.addCase(addDocumentFiles.rejected, (state: Draft<IFileUploadState>, action) => {
            state.addDocumentFilesDialogState.loading = false;
            const { files, fileDataModels } = action.meta.arg;
            const total = files.length + fileDataModels.length;
            const errorObj = safeJsonParse<IDetailedErrorMessage>(action.error.message, null);
            const count = errorObj?.detail ? parseInt(errorObj.detail) : total;
            state.addDocumentFilesDialogState.error = {
                count,
                total,
            };
        });
    },
};

export const uploadFile = createAsyncThunk(urlUploadFile, thunks.uploadFile);
export const uploadFiles = createAsyncThunk('upload/files', thunks.uploadFiles);
export const addDocumentFiles = createAsyncThunk(
    urlProjectDocumentAddFiles,
    thunks.addDocumentFiles,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IFileUploadState extends SliceProps {
    addDocumentFilesDialogState: IAddDocumentFilesDialogState;
    filesPathes: string[];
}

/**
 * Create the redux slice.
 */
export const fileUploadSlice = createSlice({
    name: 'fileUpload',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    showAddDocumentFilesDialog,
    resetAddDocumentFilesState,
    resetUtilsFileUploadState,
    resetErrorUploadFilesGeneral,
} = fileUploadSlice.actions;

/**
 * Export slice reducer as default.
 */
export default fileUploadSlice.reducer;
